import React, { useState, useEffect, useCallback } from "react";
import { ServiceResult } from "../types/api";

interface SearchControls {
  search: (term: string) => void;
  clearSearch: () => void;
}

interface UseAssignmentModalStateProps {
  isOpen: boolean;
  onClose: () => void;
  // Called on close when at least one action succeeded
  onUpdate: () => void;
  search?: SearchControls;
  // A list-loading failure, shown in the same banner as action errors
  loadError?: string | null;
}

interface AssignmentAction<T> {
  kind: "assign" | "unassign";
  id: number;
  request: () => Promise<ServiceResult<T>>;
  onSuccess: (result: ServiceResult<T>) => void;
  successMessage: string;
  failureMessage: string;
}

// Feedback and in-flight state of an assignment modal: the banner message,
// which rows have a request running, the search box, and closing. The two
// lists live in useAssignmentLists.
export const useAssignmentModalState = ({
  isOpen,
  onClose,
  onUpdate,
  search,
  loadError,
}: UseAssignmentModalStateProps) => {
  const [hasChanges, setHasChanges] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [assigningItems, setAssigningItems] = useState<Set<number>>(new Set());
  const [unassigningItems, setUnassigningItems] = useState<Set<number>>(
    new Set()
  );
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const clearSearch = search?.clearSearch;

  useEffect(() => {
    if (isOpen) return;
    setSearchTerm("");
    setError("");
    setSuccess("");
    setAssigningItems(new Set());
    setUnassigningItems(new Set());
    setHasChanges(false);
    clearSearch?.();
  }, [isOpen, clearSearch]);

  const runAction = useCallback(
    async <T>({
      kind,
      id,
      request,
      onSuccess,
      successMessage,
      failureMessage,
    }: AssignmentAction<T>) => {
      const setBusy =
        kind === "assign" ? setAssigningItems : setUnassigningItems;
      setBusy((prev) => new Set(prev).add(id));
      setError("");
      setSuccess("");

      try {
        const result = await request();
        if (result.success) {
          onSuccess(result);
          setSuccess(successMessage);
          setHasChanges(true);
        } else {
          setError(result.error || failureMessage);
        }
      } catch (err: any) {
        setError(err.message || "An unexpected error occurred");
      } finally {
        setBusy((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    []
  );

  const handleClose = () => {
    if (hasChanges) onUpdate();
    onClose();
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const term = searchTerm.trim();
    if (term) search?.search(term);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    clearSearch?.();
  };

  const bannerError = error || loadError;
  const message: { text: string; type: "error" | "success" } | null =
    bannerError
      ? { text: bannerError, type: "error" }
      : success
      ? { text: success, type: "success" }
      : null;

  return {
    message,
    setError,
    assigningItems,
    unassigningItems,
    runAction,
    searchTerm,
    setSearchTerm,
    handleSearch,
    handleClearSearch,
    handleClose,
  };
};
